import React, { useCallback, useState } from 'react';

import { Box, Flex, useTheme, Menu, MenuButton, MenuList, MenuItem } from '@chakra-ui/react';
import styled from '@emotion/styled';

import { ArrowDown16 } from 'assets/assetsV1/iconComponents';

import { NotificationBadge } from 'uiToolkitV1/NotificationBadge';
import BetaTag from 'uiToolkitV1/Tag/BetaTag';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

import { StyledNavItem } from './NavItem';

const TransientProps = ['isActive'];

const StyledMenuItem = styled(MenuItem, {
  shouldForwardProp: (prop) => !TransientProps.includes(prop),
})(({ theme, ...props }) => ({
  backgroundColor: 'white',
  '&:focus': {
    backgroundColor: 'white',
  },

  '&:hover': {
    backgroundColor: 'white',
    cursor: 'pointer',

    '& p': {
      color: `${theme.colors.text.g1} !important`,
    },
  },

  '&:active, &.active-styles': {
    backgroundColor: theme.colors.surface.E6F1FD,

    '& p': {
      color: `${theme.colors.button.blue.focus} !important`,
    },
  },
}));

const SubNavItem = ({
  Icon = null,
  HoverIcon = null,
  ActiveIcon = null,
  isActive = false,
  notificationCount = 0,
  title = '',
  subTitle = '',
}) => {
  const theme = useTheme();

  const [hovering, setHovering] = useState(false);
  const onMouseEnterHandler = useCallback(() => {
    setHovering(true);
  }, []);
  const onMouseLeaveHandler = useCallback(() => {
    setHovering(false);
  }, []);

  let IconComp;

  if (Icon) {
    IconComp = Icon;
  }

  if (hovering && !!HoverIcon && !isActive) {
    IconComp = HoverIcon;
  }

  if (isActive && !!ActiveIcon) {
    IconComp = ActiveIcon;
  }
  return (
    <StyledMenuItem
      onMouseEnter={onMouseEnterHandler}
      onMouseLeave={onMouseLeaveHandler}
      className={isActive ? 'active-styles' : ''}
      size="default"
    >
      <Flex>
        <IconComp mt="5px" fill="transparent" boxSize="24px" />
        <NotificationBadge notificationCount={notificationCount} transform="translate(2px, 2px)">
          <Flex flexDir="column" p={1}>
            <TextBodyMedium bold mb="2px" sx={{ color: `${theme.colors.text.g1} !important` }}>
              {title}
            </TextBodyMedium>
            <TextLabel sx={{ color: `${theme.colors.text.g2} !important` }} width="225px">
              {subTitle}
            </TextLabel>
          </Flex>
        </NotificationBadge>
      </Flex>
    </StyledMenuItem>
  );
};

const NavMenuItem = ({
  Icon = null,
  HoverIcon = null,
  ActiveIcon = null,
  isActive = false,
  notificationCount = 0,
  title = '',
  children,
  isBetaFeature = false,
}) => {
  const [hovering, setHovering] = useState(false);
  const [clicked, setIsClicked] = useState(false);
  const onMouseEnterHandler = useCallback(() => {
    setHovering(true);
  }, []);
  const onMouseLeaveHandler = useCallback(() => {
    setIsClicked(false);
    setHovering(false);
  }, []);

  let IconComp;

  if (Icon) {
    IconComp = Icon;
  }

  if (hovering && !!HoverIcon && !isActive) {
    IconComp = HoverIcon;
  }

  if (isActive && !!ActiveIcon) {
    IconComp = ActiveIcon;
  }

  const theme = useTheme();

  return (
    <StyledNavItem
      size="default"
      onMouseEnter={onMouseEnterHandler}
      onMouseLeave={onMouseLeaveHandler}
      onClick={(e) => {
        setIsClicked((val) => !val);
        e.stopPropagation();
      }}
      isActive={isActive}
    >
      <Menu
        isOpen={hovering || clicked}
        onClose={() => {
          setHovering(false);
        }}
      >
        <MenuButton>
          <Box display="flex" flexDir="column" alignItems="center">
            <NotificationBadge
              notificationCount={notificationCount}
              //   anchorOrigin={notificationAnchorOrigin}
              transform="translate(16px, 2px)"
            >
              <Flex alignItems="center">
                <IconComp fill="transparent" boxSize="24px" />
                {isBetaFeature && <BetaTag ml={2} />}
              </Flex>
            </NotificationBadge>
            <Flex alignItems="center">
              <TextBodyMedium bold={isActive} color="text.g2">
                {title}
              </TextBodyMedium>
              <ArrowDown16 boxSize="16px" />
            </Flex>
          </Box>
        </MenuButton>
        <MenuList border="none" boxShadow={theme.shadows.large}>
          {children}
        </MenuList>
      </Menu>
    </StyledNavItem>
  );
};

export { NavMenuItem, SubNavItem };
